import KitModal from '../../UI/KitModal';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthStore } from '../../../store/storeAuth';
import { useMemo } from 'react';
import { useReactFlow } from 'reactflow';
import Label from '../../UI/Label/Label';
import { useNotificationStore } from '../../../store/storeNotifications';
import IconButtonTooltip from '../../UI/IconButtonTooltip/IconButtonTooltip';
import { Copy } from '@phosphor-icons/react';
import { get } from 'http';
import { useStoreTabs } from '../../../store/storeTabs';
import useFlowsStore from '../../../store/storeFlows';

type Props = {
  onHide: () => void;
  show: boolean;
  formData: any;
  onChange: (e: any) => void;
  flowId?: string;
  isPublic?: boolean;
  onChangeIsPublic?: (e: any) => void;
  spaceId?: string;
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  flowId,
  isPublic,
  spaceId,
  onChangeIsPublic,
}: Props) => {
  const { getNodes } = useReactFlow();
  const { flows } = useFlowsStore(state => state);
  const { folders, user } = useAuthStore(state => state);

  const setNotification = useNotificationStore(state => state.setNotification);

  const config = useMemo(() => {
    let nodes = getNodes();
    const getAllVarInputNodes = () => {
      const varInputNodes = nodes?.filter(
        node => node?.type === 'varInputNode',
      );

      if (!varInputNodes.length) {
        return 'No var input nodes found';
      }

      const rawJson = JSON.stringify(
        varInputNodes.map((node, idx) => ({
          inputId: node?.id,
          inputText: `Example  ${idx + 1} Text For Input `,
        })),
      );

      return (
        '"variableInputs":' +
        ' \n' +
        rawJson
          .replace(/,/g, ',\n')
          .replace(/\{/g, '{\n')
          .replace(/\[/g, '[\n')
          .replace(/\}/g, '\n}')
          .replace(/\]/g, '\n]')
      );
    };
    return getAllVarInputNodes();
  }, [getNodes()]);

  const activeFlow = flows.find(flow => flow.id === flowId);

  const apiKey = user?.apiKey;

  const checkText = useMemo(() => {
    const text =
      config === 'No var input nodes found'
        ? config
        : `{
        ${config}
      }`;

    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch (e) {
      return config;
    }
  }, [config]);

  const copyFlow = () => {
    navigator.clipboard.writeText(activeFlow?.id!);
    setNotification({
      message: 'Copied to clipboard',
      type: 'success',
    });
  };

  const copyFlowEmail = () => {
    navigator.clipboard.writeText(
      activeFlow?.id! + '@' + process.env.REACT_APP_FLOW_EMAIL,
    );
    setNotification({
      message: 'Copied to clipboard',
      type: 'success',
    });
  };

  const generateLinkForFlowId = useMemo(() => {
    if (window.location.origin.includes('localhost')) {
      return `http://localhost:3001/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('staging')) {
      return `https://api-staging.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('app')) {
      return `https://api.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('dev')) {
      return `https://api-dev.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }
  }, [flowId]);

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '690px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Box>
        <Label isAdvanced labelName="Settings" />
        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            mb: '4px',
          }}
        >
          Flow Name:{' '}
          <span
            style={{
              color: '#98A2B3',
            }}
          >
            {activeFlow?.name}
          </span>
        </Typography>

        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            mb: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          Flow ID:{' '}
          <span
            style={{
              color: '#98A2B3',
            }}
          >
            {activeFlow?.id}
          </span>
          <IconButtonTooltip title="Copy Flow Id" onClick={copyFlow}>
            <Copy size={14} color="#475467" />
          </IconButtonTooltip>
        </Typography>

        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            mb: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          Flow Email:{' '}
          <span
            style={{
              color: '#98A2B3',
            }}
          >
            {activeFlow?.id}@{process.env.REACT_APP_FLOW_EMAIL}
          </span>
          <IconButtonTooltip title="Copy Flow Email" onClick={copyFlowEmail}>
            <Copy size={14} />
          </IconButtonTooltip>
        </Typography>
      </Box>

      <Divider
        sx={{
          mb: '24px',
          mt: '8px',
        }}
      />

      <Box>
        <Label isAdvanced labelName="API Settings" />

        <FormControlLabel
          name="generateSessionId"
          onChange={onChange}
          checked={formData.generateSessionId}
          control={<Switch />}
          sx={{
            ml: 0,
            color: '#475467',
          }}
          labelPlacement="start"
          label="Allow passing in custom ID"
        />
      </Box>

      <Box>
        <FormControlLabel
          name="isPublic"
          onChange={onChangeIsPublic}
          checked={isPublic}
          control={<Switch />}
          sx={{
            ml: 0,
            color: '#475467',
          }}
          labelPlacement="start"
          label="Make flow public"
        />
      </Box>

      <Divider
        sx={{
          mb: '24px',
          mt: '8px',
        }}
      />

      <Box>
        <Label
          sx={{
            mb: '16px',
            // color: user?.theme === 'dark' ? '#D0D5DD' : 'black'
          }}
          labelName="API Configuration"
          isAdvanced
        />

        <TextField
          fullWidth
          value={generateLinkForFlowId}
          size="medium"
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />
        <Typography
          sx={{
            mt: '8px',
            color: '#475467',
            fontSize: '12px',
          }}
          variant={'body1'}
        >
          You can use the following link to access the API for the flow.
        </Typography>

        <Box
          sx={{
            mb: '24px',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Button
            onClick={() => {
              navigator.clipboard.writeText(generateLinkForFlowId!);
              setNotification({
                message: 'Link Copied to clipboard',
                type: 'success',
              });
            }}
          >
            Copy Link
          </Button>
        </Box>

        <TextField
          fullWidth
          size="medium"
          value={'api-key' + ' : ' + apiKey}
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />
        <Typography
          sx={{
            mt: '8px',
            color: '#475467',
            fontSize: '12px',
          }}
          variant={'body1'}
        >
          Use the following API Key to authenticate the API request.
        </Typography>

        <Box
          sx={{
            mb: '24px',
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Button
            onClick={() => {
              navigator.clipboard.writeText(apiKey!);
              setNotification({
                message: 'Api Key Copied to clipboard',
                type: 'success',
              });
            }}
          >
            Copy API Key
          </Button>
        </Box>
      </Box>
      <Box>
        <TextField
          multiline
          rows={5}
          fullWidth
          value={checkText}
          InputProps={{
            readOnly: true,
            style: {
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            },
          }}
          sx={{
            color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
            border: `2px solid ${
              user?.theme === 'dark' ? '#475467' : '#EAECF0'
            }`,
          }}
        />
      </Box>
      <Typography
        sx={{
          mt: '8px',
          color: '#475467',
          fontSize: '12px',
        }}
        variant={'body1'}
      >
        This is the API configuration for the flow. You can use this to generate
        the API for the flow.
      </Typography>

      <Box
        sx={{
          mb: '24px',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button
          onClick={() => {
            navigator.clipboard.writeText(checkText);
            setNotification({
              message: 'Api Config Body Copied to clipboard',
              type: 'success',
            });
          }}
        >
          Copy API Config Body
        </Button>
      </Box>
    </KitModal>
  );
};

export default SettingsModal;
